import React, {useState, useEffect, useRef} from 'react';
import {Link, useParams, useNavigate, useSearchParams} from 'react-router-dom';
import {IoCheckmarkDoneCircleOutline} from "react-icons/io5";
import {MdOutlinePendingActions} from "react-icons/md";
import styles from "./ClientProgressDashboard.module.css";
import Swal from "sweetalert2";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import ClientSelectSearch from "../ClientSelectSearch/ClientSelectSearch";
import ClientAccountActions from "../ClientAccountActions/ClientAccountActions";
import IdUploader from "../../IdUploader/IdUploader";
import IdDisplay from "../../IdDisplay/IdDisplay";
import SelectTaxYear from "../SelectTaxYear/SelectTaxYear";
import {useTaxYear} from "../../../contexts/TaxYearContext";
import useApi from "../../../utils/useApi";
import Button from "../../UI/Button";
import {Loader2} from "lucide-react";

const MetricCard = ({
                        title,
                        specialTitle,
                        longText,
                        value,
                        additionalInfo = [],
                        data = null,
                        button = null,
                        showDataIndex,
                        textClassName,
                        className,
                        btnClassName,
                        showIcon,
                        clientProgress,
                        handleButtonClick,
                        handleRedirect,
                        onCreatePaymentLink,
                        onDeletePaymentLink,
                        deletingPayment,
                        generatingInvoice,
                        handleShowIdUploader,
                        showIdUploader
                    }) => {
    const labelMap = {
        bank_name: "Bank Name",
        routing_number: "Routing Number",
        account_number: "Account Number"
    };

    const arrayData = data ? Object.entries(data) : null;

    return (
        <div className={`p-4 bg-gray-50 w-full dark:bg-gray-800 rounded-lg shadow-sm relative ${className || ''}`}>
            <div className="flex flex-row justify-start gap-2 items-center mb-2">
                <h3 className="text-sm text-gray-600 dark:text-white">{specialTitle ?? title}</h3>
                {showIcon && (
                    <div className={textClassName}>
                        {clientProgress?.returnAccepted ?
                         <IoCheckmarkDoneCircleOutline size={30}/> :
                         <MdOutlinePendingActions size={30}/>
                        }
                    </div>
                )}
                {button?.showPaymentCompleteIcon && (
                    <div className={textClassName}>
                        <IoCheckmarkDoneCircleOutline size={30}/>
                    </div>
                )}
            </div>

            <p className={`text-2xl font-semibold ${textClassName || 'text-blue-600'}`}>{value}</p>
            {longText && (
                <p className="text-sm text-gray-600 dark:text-white mt-1">{longText}</p>
            )}

            {additionalInfo.map((info, index) => (
                <p key={index} className="text-sm text-gray-600 dark:text-white mt-1">{info}</p>
            ))}

            {arrayData && arrayData.map(([key, val], index) => {
                const displayKey = labelMap[key] ?? key;
                const displayValue = val ?? "N/A";
                return (
                    <p key={index} className="text-sm text-gray-600 dark:text-white mt-1">
                        {showDataIndex && <span className="font-bold">{displayKey}:</span>}
                        <span className="ml-1">{displayValue}</span>
                    </p>
                );
            })}

            {button && (
                <div className="mt-2">
                    {button.type === 'updateProfile' && (
                        <div className="flex items-center flex-row gap-2">
                            <Button onClick={(e) => handleButtonClick(e, button.param, button)}
                                    dataValue={button.options.no}
                                    variant={'danger'}
                                    className="text-sm mt-1">
                                No
                            </Button>
                            <Button onClick={(e) => handleButtonClick(e, button.param, button)}
                                    dataValue={button.options.yes}
                                    className="text-sm mt-1">
                                Yes
                            </Button>
                        </div>
                    )}
                    {button.type === 'redirect' && (
                        <Button onClick={(e) => handleRedirect(button.link, button?.useWindow)}
                                className="text-sm mt-1">
                            {button.label}
                        </Button>
                    )}
                    {button.type === 'payment' && (
                        <div className={`flex flex-row justify-between`}>
                            <Button onClick={(e) => onCreatePaymentLink(button.amount)}
                                    className={`text-sm mt-1 ${btnClassName}`}
                                    variant={button.variant}
                            >
                                {generatingInvoice ? (
                                    <>
                                        Generating... <Loader2 className="w-4 h-4 animate-spin"/>
                                    </>

                                ) : (
                                     button.label
                                 )}

                            </Button>
                            {button.showDelete && (
                                <Button onClick={(e) => onDeletePaymentLink(button.paymentId)}
                                        className={`text-sm mt-1 ${btnClassName}`}
                                        variant={'danger'}
                                >
                                    {deletingPayment ? (
                                        <>
                                            Deleting... <Loader2 className="w-4 h-4 animate-spin"/>
                                        </>

                                    ) : (
                                         button.deleteLabel
                                     )}
                                </Button>
                            )}

                        </div>
                    )}
                    {button.type === 'uploadId' && (
                        <Button onClick={(e) => handleShowIdUploader(button)}
                                variant={showIdUploader ? button.variant : 'primary'}
                                className={`text-sm mt-1 ${button.classTrue}`}>
                            {showIdUploader ? (
                                <span>{button.labelTrue}</span>
                            ) : (
                                 <span>{button.labelFalse}</span>
                             )}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

const InfoSection = ({ title, fields }) => (
    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg h-full">
        <h3 className="font-medium text-gray-900 dark:text-white mb-4">{title}</h3>
        <div className="space-y-2">
            {Object.entries(fields).map(([label, value], index) => (
                <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-white">{label}</span>
                    <span className="text-gray-900 dark:text-white capitalize">{value || 'N/A'}</span>
                </div>
            ))}
        </div>
    </div>
);

const ProgressSteps = ({ steps, currentStep, totalSteps }) => (
    <div className="mb-8">
        <div className={`${styles.ProgressStepsContainer} flex items-center justify-between align-center`}>
            {steps.map((step, index) => (
                <div
                    key={index}
                    data-step={index}
                    className={`flex items-center ${index + 1 === totalSteps ? '' : 'flex-1'} `}
                >
                    <div
                        className={`w-12 h-8 flex items-center justify-center rounded-full
                ${
                            index < currentStep || currentStep === true
                            ? 'bg-green-600 text-white'
                            : index === currentStep
                              ? 'bg-yellow-200 text-black border-2 border-yellow-300'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-white'
                        }
                ${index + 1 === totalSteps ? 'w-8' : ''}
              `}
                    >
                        {index + 1}
                    </div>
                    {index < steps.length - 1 && (
                        <div
                            className={`h-1 w-full mx-2 ${
                                index < currentStep || currentStep === true ? 'bg-green-600' : 'bg-gray-200'
                            }`}
                        />
                    )}
                </div>
            ))}
        </div>
        <div className="flex justify-between mt-2">
            {steps.map((step, index) => (
                <div key={index} className={`${styles.ProgressStepLabel} text-xs text-gray-600 w-24 text-center`}>
                    {step.label}
                </div>
            ))}
        </div>
    </div>
);

const ClientProgressDashboard = ({ user, handleShowHeader, handleShowSettingsHeader }) => {
    const { uid } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { taxYear, updateTaxYear } = useTaxYear();
    const containerRef = useRef(null);

    // State
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    const [deletingPayment, setDeletingPayment] = useState(false);
    const [loadingFadeOut, setLoadingFadeOut] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [existingId, setExistingId] = useState(null);
    const [idExpiry, setIdExpiry] = useState("");
    const [availableYears, setAvailableYears] = useState([]);
    const [showIdUploader, setShowIdUploader] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [client, setClient] = useState(null);
    const [clients, setClients] = useState([]);
    const [currentClient, setCurrentClient] = useState(null);
    const [showAgreement, setShowAgreement] = useState(false);
    const [agreeLoading, setAgreeLoading] = useState(false);

    const { createPaymentLink, deletePaymentLink, getAdminClientDashboard, getClientIdentification, updateProfileStatus, readyToFile } = useApi();

    // Loading fade effect
    useEffect(() => {
        if (!isLoading && !loadingFadeOut) {
            setLoadingFadeOut(true);
            setTimeout(() => {
                setShowContent(true);
                setLoadingFadeOut(false);
            }, 300);
        }
    }, [isLoading]);

    // Initialize dashboard
    useEffect(() => {
        initialize();
    }, [taxYear]);

    // Handle header and click outside
    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);

        if (user.user_type === 'admin') {
            const handleClickOutside = (e) => {
                if (isDropdownOpen && containerRef.current && !containerRef.current.contains(e.target)) {
                    setIsDropdownOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isDropdownOpen, handleShowHeader, handleShowSettingsHeader, user.user_type]);

    const initialize = async () => {
        setIsLoading(true);
        try {
            const data = { year: taxYear };
            let response;

            if (user.user_type === 'admin') {
                response = await getAdminClientDashboard(uid, data);
            } else {
                response = await getAdminClientDashboard(user.id, data);
            }

            setDashboardData(response);
            setClient(response.user);
            setIdExpiry(response.user.profile.identification_expiration_date);
            setExistingId(response.id);
            setAvailableYears(response.availableYears);

            if (response?.clients) {
                setClients(response.clients);
                const current = response.clients.find((client) => client.id == uid);
                setCurrentClient(current || null);
            }
        }
        catch (error) {
            console.error('Error loading dashboard data:', error);
        }
        finally {
            setIsLoading(false);
        }
    };

    const refreshIds = async () => {
        const response = await getClientIdentification(client.id);
        setShowIdUploader(false);
        setExistingId(response.id);
        setIdExpiry(response.identification_expiration_date);
        initialize();
    };

    const handleButtonClick = async (e, param, info) => {
        const value = e.currentTarget.dataset.value;
        const data = {
            param: param,
            value: value,
            year: taxYear,
        };

        try {
            let response;
            if (user.user_type === 'admin') {
                response = await updateProfileStatus(uid, data);
            } else {
                response = await updateProfileStatus(user.id, data);
            }

            if (response.success) {
                if ((value == 0 && info.options.no == 0 || value == 1 && info.options.no == 1) && info.redirectOnNo) {
                    navigate(info.redirectOnNo);
                } else {
                    await Swal.fire({
                                        title: 'Success!',
                                        text: response.message,
                                        icon: 'success',
                                        timer: 2000,
                                        showConfirmButton: true
                                    });
                    initialize();
                }
            }
        }
        catch (error) {
            await Swal.fire({
                                title: 'Error!',
                                text: error.message,
                                icon: 'error',
                                showConfirmButton: true
                            });
        }
    };

    const handleCreatePaymentLink = async (amount) => {
        setGeneratingInvoice(true);
        const data = {
            product_name: client.profile.client + ' Tax Preparation ',
            amount: amount,
            uid: client.id,
            year: taxYear,
        };

        const response = await createPaymentLink(data);

        if (response.success) {
            initialize();
            await Swal.fire({
                                title: 'Success!',
                                text: response.message,
                                icon: 'success',
                                showCancelButton: true,
                                confirmButtonText: response.buttonLabel,
                                confirmButtonColor: '#3085d6',
                                cancelButtonText: 'Continue',
                                cancelButtonColor: '#6c757d',
                                preConfirm: () => {
                                    window.open(response.url, '_blank');
                                }
                            });
        }

        setGeneratingInvoice(false);
    };

    const handleDeletePaymentLink = async (id) => {
        setDeletingPayment(true);
        try {
            const response = await deletePaymentLink(id);
            if (response.success) {
                setErrorMessage(null);
                setSuccessMessage(response.message);
            } else {
                setSuccessMessage(null);
                setErrorMessage(response.message);
            }
        }
        catch (err) {
            setSuccessMessage(null);
            setErrorMessage(err.message);
        }
        setDeletingPayment(false);
        setPaymentDeleteTimer();
    }

    const setPaymentDeleteTimer = () => {
        setTimeout(() => {
            setSuccessMessage(null);
            setErrorMessage(null);
            initialize();
        }, 1500);
    }

    const handleRedirect = (link, useWindow) => {
        if (useWindow) {
            window.open(link, '_blank', 'noopener,noreferrer');
        } else {
            navigate(link);
        }
    };

    const handleReadyToFile = async () => {
        setAgreeLoading(true);
        try {
            const data = { readyToFile: 'Yes' };
            const response = await readyToFile(client.id, data);
            await Swal.fire({
                                title: 'Success!',
                                text: response.message,
                                icon: 'success',
                                confirmButtonText: 'Continue',
                                confirmButtonColor: '#3085d6'
                            });
            initialize();
        }
        finally {
            setAgreeLoading(false);
        }
    };

    const getCardConfig = () => ({
        mainCards: [
            {
                id: 'currentStep',
                longText: dashboardData?.mainMetrics?.currentStep?.longText,
                specialTitle: dashboardData?.mainMetrics?.currentStep?.specialTitle,
                title: user.user_type === 'admin' ? "Client Current Step" : "Current Step",
                value: dashboardData?.mainMetrics?.currentStepLabel || 'N/A',
                data: dashboardData?.mainMetrics?.currentStep?.data,
                button: dashboardData?.mainMetrics?.currentStep?.buttons,
                showDataIndex: dashboardData?.mainMetrics?.currentStep?.showDataIndex,
                textClassName: dashboardData?.mainMetrics?.currentStep?.textClassName,
                showIcon: dashboardData?.clientProgress?.isSubmitted || dashboardData?.clientProgress?.returnAccepted,
                className: 'flex-1'
            },
            {
                id: 'paymentLink',
                title: dashboardData?.mainMetrics?.paymentLink?.title,
                button: dashboardData?.mainMetrics?.paymentLink?.buttons,
                value: dashboardData?.mainMetrics?.paymentLink?.amountLabel,
                textClassName: dashboardData?.mainMetrics?.paymentLink?.buttons?.className,
                btnClassName: dashboardData?.mainMetrics?.paymentLink?.buttons?.btnClassName,
                className: 'flex-1'
            }
        ],
        metricsGrid: [
            {
                id: 'uploadId',
                title: "Upload Id",
                button: {
                    type: 'uploadId',
                    labelTrue: 'Done uploading',
                    labelFalse: 'Start upload',
                    variant: 'success'
                }
            },
            {
                id: 'prepFee',
                title: "Prep Fee",
                value: `$${dashboardData?.mainMetrics?.balance || '0'}`
            },
            {
                id: 'refundAmount',
                title: "Refund Amount",
                value: `$${dashboardData?.mainMetrics?.refundAmount || '0'}`
            },
            {
                id: 'additionalFees',
                title: "Additional Fees",
                value: `$${dashboardData?.mainMetrics?.addtFees || '0'}`
            },
            {
                id: 'accountDocs',
                title: "Account Documents",
                value: dashboardData?.mainMetrics?.accountDocuments || '0',
                isLink: true,
                linkPath: `/documents/${user.id}`
            },
            {
                id: 'finishedDocs',
                title: "Finished Documents",
                value: dashboardData?.mainMetrics?.finishedDocuments || '0',
                isLink: true,
                linkPath: `/documents/${user.id}`
            }
        ],
        adminNotes: user.user_type === 'admin' ? [
            {
                id: 'adminNotes',
                title: "",
                data: dashboardData?.mainMetrics?.notesData,
                showDataIndex: true,
                className: 'lg:col-span-3 md:col-span-3 col-span-3'
            }
        ] : [],
        bottomSection: [
            {
                id: 'accountInfo',
                title: "Account Information",
                fields: {
                    'Account Type': dashboardData?.bottomSection?.accountType,
                    'Account User': dashboardData?.bottomSection?.accountUser,
                }
            },
            {
                id: 'clientInfo',
                title: "Client Information",
                fields: {
                    Client: dashboardData?.bottomSection?.client,
                    Spouse: dashboardData?.bottomSection?.spouse,
                }
            },
            {
                id: 'documentStatus',
                title: `Document Status (${taxYear})`,
                fields: {
                    'Documents Needed': dashboardData?.mainMetrics?.documentsNeeded,
                    'Documents Progress': `${dashboardData?.mainMetrics?.finishedDocuments || 0}/${
                        dashboardData?.mainMetrics?.documentsNeeded || 0
                    }`,
                    'Total Documents': dashboardData?.mainMetrics?.accountDocuments || 0
                },
                isLink: true,
                linkPath: `/documents/${uid}`
            }
        ],
        feeBreakdown: [
            {
                id: 'feeBreakdown',
                value: "Additional Fee Breakdown",
                data: dashboardData?.mainMetrics?.addtFeeBreakdown,
                showDataIndex: true,
                className: 'col-span-3',
                show: dashboardData?.mainMetrics?.addtFees > 0
            }
        ]
    });

    const cardConfig = getCardConfig();

    if (isLoading || loadingFadeOut) {
        return (
            <LoadingScreen className={`flex items-center justify-center h-64 animate-fade-in ${loadingFadeOut ? 'animate-fade-out' : ''}`}/>
        );
    }

    if (!dashboardData) {
        return (
            <div className="flex items-center justify-center h-64 page-container relative z-10">
                <div className="text-gray-600">No data available on this client. They may need to login and create a profile or they may have been deleted.</div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-white dark:bg-gray-900 page-container relative z-10 animate-fade-in">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 w-full">
                {user.user_type === 'admin' && (
                    <div className="w-full md:w-96">
                        <ClientSelectSearch
                            clients={clients}
                            includeLink={true}
                            currentClient={currentClient}
                            onSelectClient={(client) => setCurrentClient(client)}
                        />
                    </div>
                )}
                <SelectTaxYear availableYears={availableYears} includeText={true}/>
            </div>

            {/* Agreement Section */}
            {dashboardData.clientProgress?.currentStep === dashboardData.clientProgress?.totalSteps - 1 && (
                <div className="px-8 w-full mb-6">
                    {!showAgreement ? (
                        <div className="font-bold">
                            <h2 className="text-2xl text-center mb-8">You are ready to file!</h2>
                            <p className="mb-4">
                                Please make sure all steps are completed prior to submitting your information to us.
                                Once submitted, we will review all information that has been either uploaded to your
                                portal or dropped off in the office. If we are missing any documentation, we will
                                reach out and let you know what additional information may be needed. It will only
                                be given to our tax preparers upon a completed file.
                                Turn around time for all completed uploads or drop off returns will be 1-2 weeks.
                            </p>
                            <p className="mb-4">
                                Signatures and payment are required at the time of completion and prior to the
                                return being submitted.
                            </p>
                            <p className="mb-4">I acknowledge the above statement and I am ready to submit.</p>
                            <div className="flex justify-center">
                                <button
                                    onClick={() => setShowAgreement(true)}
                                    className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                    Yes
                                </button>
                            </div>
                        </div>
                    ) : (
                         <div className="font-bold">
                             <p className="mb-8">
                                 I acknowledge I provided information to McKinney Tax Services for the purpose of tax
                                 preparation and that the information provided is true and correct, to the best of my
                                 knowledge. In the event that I decide not to have McKinney Tax Services file my taxes,
                                 I agree to pay an incompletion fee to McKinney Tax Services.
                             </p>
                             <p>
                                 I also understand that if I schedule an appointment that I do not show up for, or give a
                                 24-hour cancellation notice, I will be charged a $50 cancellation fee.
                             </p>
                             <div className="flex justify-center mt-8 space-x-4">
                                 <button
                                     onClick={() => setShowAgreement(false)}
                                     className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                                     disabled={agreeLoading}
                                 >
                                     Cancel
                                 </button>
                                 <button
                                     onClick={handleReadyToFile}
                                     disabled={agreeLoading}
                                     className={`px-6 py-2 rounded text-white
                    ${agreeLoading ? 'bg-blue-400 cursor-not-allowed animate-pulse' : 'bg-blue-500 hover:bg-blue-600'}
                    transition-colors duration-200`}
                                 >
                                     {agreeLoading ? 'Processing...' : 'I Agree'}
                                 </button>
                             </div>
                         </div>
                     )}
                </div>
            )}

            {/* Progress Steps */}
            {dashboardData?.clientProgress && (
                <ProgressSteps
                    steps={dashboardData.clientProgress.stepLabels}
                    currentStep={dashboardData.clientProgress.currentStep}
                    totalSteps={dashboardData.clientProgress.totalSteps}
                />
            )}

            {/* Card Sections */}
            {showIdUploader && (
                <IdUploader user={user} uid={uid} api={useApi} existingId={existingId} onRefresh={refreshIds}/>
            )}
            <h2 className={`flex flex-row w-full py-4`}>Tax Preparation {taxYear}</h2>

            {user.user_type === 'admin' && (
                <ClientAccountActions user={client} className="mb-4" year={taxYear} includeProfileBtn={true} onArchiveSuccess={initialize}/>
            )}

            {(errorMessage || successMessage) && (
                <div className="flex flex-row w-full py-4">
                    {successMessage && (
                        <h3 className={`text-green-500`}>{successMessage}</h3>
                    )}
                    {errorMessage && (
                        <h3 className={`text-red-500`}>{errorMessage}</h3>
                    )}
                </div>
            )}


            {/* Main Cards */}
            <div className="flex flex-row items-stretch gap-4 w-full items-center mt-2 mb-4">
                {getCardConfig().mainCards.map(card => (
                    <MetricCard
                        key={card.id}
                        {...card}
                        clientProgress={dashboardData.clientProgress}
                        handleButtonClick={handleButtonClick}
                        handleRedirect={handleRedirect}
                        onCreatePaymentLink={handleCreatePaymentLink}
                        onDeletePaymentLink={handleDeletePaymentLink}
                        handleShowIdUploader={() => setShowIdUploader(!showIdUploader)}
                        showIdUploader={showIdUploader}
                        deletingPayment={deletingPayment}
                        generatingInvoice={generatingInvoice}
                    />
                ))}
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {getCardConfig().metricsGrid.map(card => (
                    card.isLink ? (
                        <Link key={card.id} to={card.linkPath} className="hover:opacity-90 transition-opacity">
                            <MetricCard {...card} />
                        </Link>
                    ) : (
                        <MetricCard
                            key={card.id}
                            {...card}
                            handleShowIdUploader={() => setShowIdUploader(!showIdUploader)}
                            showIdUploader={showIdUploader}
                        />
                    )
                ))}
                {getCardConfig().adminNotes.map(card => (
                    <MetricCard key={card.id} {...card} />
                ))}
                {/* Fee Breakdown */}
                <div className="w-full col-span-3 md:col-span-3 lg:col-span-3 mb-2">
                    {getCardConfig().feeBreakdown.map(card => (
                        card.show && (
                            <MetricCard key={card.id} {...card} />
                        )
                    ))}
                </div>
                <IdDisplay
                    user={user}
                    client={client}
                    documents={existingId}
                    onRefresh={refreshIds}
                />
            </div>

            {/* Bottom Section Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                {getCardConfig().bottomSection.map(section => (
                    section.isLink ? (
                        <Link key={section.id} to={section.linkPath}>
                            <InfoSection
                                title={section.title}
                                fields={section.fields}
                            />
                        </Link>
                    ) : (
                        <div key={section.id} className="h-full w-full">
                            <InfoSection
                                title={section.title}
                                fields={section.fields}
                            />
                        </div>
                    )
                ))}
            </div>


        </div>
    );
};

export default ClientProgressDashboard;
